import * as React from 'react';

import { Box, type BoxProps } from '../../components/ui/box';
import { Input } from '../../components/ui/input';
import { Popover, PopoverPopup, PopoverTrigger } from '../../components/ui/popover';
import { Text } from '../../components/ui/text';
import { WASH_ACTIVE, WASH_HOVER } from '../../components/ui/box-classes';
import { cn } from '../../lib/utils';

import {
  EntitySelector,
  EntitySelectorInput,
  EntitySelectorPool,
  EntitySelectorSelectedChips,
} from '../entity-selector';
import type {
  EntityItemConfig,
  EntitySelection,
  EntitySelectorConfig,
} from '../entity-selector';

import { DataTableCellContent } from './data-table-cell';
import {
  useDataTableDensity,
  useDataTableSurface,
} from './data-table-context';
import { useDataTableOverlay } from './data-table-overlay';
import type { DataTableTextBrightness } from './data-table-types';
import {
  CELL_ALIGNMENT_CLASS,
  DENSITY_CONFIG,
  type DataTableCellAlignment,
} from './data-table-utils';
import type { Omittable } from "../../lib/props";

const TEXT_ALIGN_CLASS = {
  start: 'text-left',
  center: 'text-center',
  end: 'text-right',
} as const;

type DataTableCellTriggerProps = Omit<
  BoxProps<'button'>,
  'as' | 'align' | 'children'
> &
  Omittable<{
    align?: DataTableCellAlignment;
    disabled?: boolean;
    active?: boolean;
    /** Optional so the trigger can be a Base UI `render` element — the primitive
     *  supplies children at render time (same idiom as vendored `render={<Button/>}`). */
    children?: React.ReactNode;
  }>;

function DataTableCellTrigger({
  align = 'start',
  disabled,
  active = false,
  className,
  children,
  ...props
}: DataTableCellTriggerProps) {
  const surface = useDataTableSurface();
  return (
    <Box<'button'>
      flex={1}
      minWidth={0}
      fill="width"
      alignSelf="stretch"
      direction="row"
      align="center"
      overflow="hidden"
      rounded="sm"
      borderColor="transparent"
      as="button"
      disabled={disabled}
      className={cn(
        'h-full border',
        CELL_ALIGNMENT_CLASS[align],
        active
          ? cn('border-primary', WASH_ACTIVE)
          : cn('hover:border-border/60', WASH_HOVER),
        className
      )}
      {...props}
    >
      {children}
    </Box>
  );
}

export type DataTableEditableTextCellProps = Omittable<{
  value: string;
  onCommit: (nextValue: string) => void;
  placeholder?: string;
  emptyLabel?: string;
  disabled?: boolean;
  align?: DataTableCellAlignment;
  textBrightness?: DataTableTextBrightness;
  commitOnBlur?: boolean;
  'aria-label'?: string;
  editing?: boolean;
  onEditingChange?: (editing: boolean) => void;
}>;

export function DataTableEditableTextCell({
  value,
  onCommit,
  placeholder,
  emptyLabel = '—',
  disabled,
  align = 'start',
  textBrightness = 'high',
  commitOnBlur = true,
  'aria-label': ariaLabel,
  editing,
  onEditingChange,
}: DataTableEditableTextCellProps) {
  const density = useDataTableDensity();
  const densityConfig = DENSITY_CONFIG[density];
  const resolvedAriaLabel = ariaLabel ?? 'Edit cell value';
  const [uncontrolledEditing, setUncontrolledEditing] = React.useState(false);
  const isEditingControlled = editing !== undefined;
  const isEditing = isEditingControlled ? editing : uncontrolledEditing;
  const [draft, setDraft] = React.useState(value);
  const didSubmitRef = React.useRef(false);

  const setEditing = React.useCallback(
    (nextEditing: boolean) => {
      if (!isEditingControlled) {
        setUncontrolledEditing(nextEditing);
      }
      onEditingChange?.(nextEditing);
    },
    [isEditingControlled, onEditingChange]
  );

  React.useEffect(() => {
    if (!isEditing) {
      setDraft(value);
    }
  }, [value, isEditing]);

  const startEditing = React.useCallback(() => {
    if (disabled) return;
    didSubmitRef.current = false;
    setDraft(value);
    setEditing(true);
  }, [disabled, value, setEditing]);

  const finishEditing = React.useCallback(
    (nextValue: string) => {
      if (disabled) return;
      setEditing(false);
      if (nextValue !== value) {
        onCommit(nextValue);
      }
    },
    [disabled, onCommit, setEditing, value]
  );

  const cancelEditing = React.useCallback(() => {
    setDraft(value);
    setEditing(false);
  }, [setEditing, value]);

  const handleKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Escape') {
        cancelEditing();
      }
      if (event.key === 'Enter') {
        didSubmitRef.current = true;
        finishEditing(draft);
      }
    },
    [cancelEditing, finishEditing, draft]
  );

  if (isEditing) {
    return (
      <Input
        unstyled
        nativeInput
        value={draft}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          setDraft(event.target.value)
        }
        onBlur={() => {
          if (commitOnBlur && !didSubmitRef.current) {
            finishEditing(draft);
          } else {
            setEditing(false);
          }
        }}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        autoFocus
        className={cn(
          'h-full w-full bg-transparent px-0',
          TEXT_ALIGN_CLASS[align],
          densityConfig.textSize
        )}
        aria-label={resolvedAriaLabel}
      />
    );
  }

  const displayValue = value?.length ? value : emptyLabel;
  const variant = value?.length
    ? textBrightness === 'subtle'
      ? 'muted'
      : undefined
    : 'muted';

  return (
    <DataTableCellTrigger
      align={align}
      disabled={disabled}
      onClick={startEditing}
      aria-label={resolvedAriaLabel}
    >
      <DataTableCellContent align={align}>
        {/* size={null} suppresses Text's default responsive size classes so the
            density text-size class applies without a sm: residue. */}
        <Text
          size={null}
          variant={variant}
          className={cn(TEXT_ALIGN_CLASS[align], densityConfig.textSize)}
        >
          {displayValue}
        </Text>
      </DataTableCellContent>
    </DataTableCellTrigger>
  );
}

export type DataTablePopoverCellProps = Omittable<{
  children: React.ReactNode;
  content: React.ReactNode | ((close: () => void) => React.ReactNode);
  align?: DataTableCellAlignment;
  disabled?: boolean;
  /**
   * Fires on every open transition — the hook for draft-on-close commits.
   * There is deliberately no `open` prop: the press gesture belongs to Base UI
   * (standalone) or to the shared overlay host (inside a table), so open state
   * has exactly one owner and a caller cannot drive it out of sync.
   */
  onOpenChange?: (open: boolean) => void;
  contentProps?: Omit<React.ComponentProps<typeof PopoverPopup>, 'children'>;
}>;

/**
 * A cell whose press opens a popover.
 *
 * Inside a DataTable this mounts NO overlay root of its own: it renders a plain
 * button and hands the table's shared popover host an anchor plus a content
 * thunk. That is what keeps a table of these cells cheap — see
 * `data-table-overlay.tsx` for why a root per cell is the thing to avoid.
 * Outside a DataTable there is no host, so it falls back to owning a popover.
 */
export function DataTablePopoverCell({
  children,
  content,
  align = 'start',
  disabled,
  onOpenChange,
  contentProps,
}: DataTablePopoverCellProps) {
  const overlay = useDataTableOverlay();
  const cellId = React.useId();
  const triggerRef = React.useRef<HTMLButtonElement | null>(null);

  // Hosted, the open cell is whichever one the table's single popover is
  // anchored to; standalone, Base UI owns the popup and this only mirrors it
  // for the trigger's `active` styling.
  const [standaloneOpen, setStandaloneOpen] = React.useState(false);
  const currentOpen =
    overlay !== null ? overlay.activePopoverId === cellId : standaloneOpen;

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      setStandaloneOpen(nextOpen);
      onOpenChange?.(nextOpen);
    },
    [onOpenChange]
  );

  const close = React.useCallback(() => handleOpenChange(false), [handleOpenChange]);

  // Normalize to a thunk so the shared host never builds content until it
  // opens — a static `content` node is constructed for every cell on every
  // render, closed popup or not.
  const contentRef = React.useRef(content);
  contentRef.current = content;
  const resolveContent = React.useCallback(
    (hostClose: () => void) => {
      const current = contentRef.current;
      return typeof current === 'function' ? current(hostClose) : current;
    },
    []
  );

  // Base UI dismisses a popup on POINTERDOWN, and this trigger sits outside
  // the popup (the host anchors manually, so there is no Popover.Trigger part
  // to register it). A press on the open cell therefore dismisses it before
  // the click arrives, and the click would reopen what the press just closed.
  // Decide the toggle on pointerdown — the same thing Popover.Trigger does —
  // and swallow the click that gesture produces. Keyboard activation emits a
  // click with no preceding pointerdown, so it still routes through onClick.
  const swallowClickRef = React.useRef(false);

  const handlePointerDown = React.useCallback(() => {
    if (!overlay || overlay.activePopoverId !== cellId) return;
    overlay.closeCellPopover();
    swallowClickRef.current = true;
  }, [overlay, cellId]);

  const handleClick = React.useCallback(() => {
    if (swallowClickRef.current) {
      swallowClickRef.current = false;
      return;
    }
    const anchor = triggerRef.current;
    if (!overlay || !anchor) return;
    overlay.openCellPopover({
      id: cellId,
      anchor,
      content: resolveContent,
      contentProps,
      onOpenChange: handleOpenChange,
    });
  }, [overlay, cellId, resolveContent, contentProps, handleOpenChange]);

  if (overlay !== null) {
    return (
      <DataTableCellTrigger
        ref={triggerRef}
        align={align}
        disabled={disabled}
        active={currentOpen}
        aria-haspopup="dialog"
        aria-expanded={currentOpen}
        onPointerDown={handlePointerDown}
        onClick={handleClick}
      >
        <DataTableCellContent align={align}>{children}</DataTableCellContent>
      </DataTableCellTrigger>
    );
  }

  const resolvedContent = currentOpen ? resolveContent(close) : null;

  return (
    <Popover onOpenChange={handleOpenChange}>
      <PopoverTrigger
        render={
          <DataTableCellTrigger
            align={align}
            disabled={disabled}
            active={currentOpen}
          />
        }
      >
        <DataTableCellContent align={align}>{children}</DataTableCellContent>
      </PopoverTrigger>
      <PopoverPopup align="start" {...contentProps}>
        {resolvedContent}
      </PopoverPopup>
    </Popover>
  );
}

// ---------------------------------------------------------------------------
// DataTableEntitySelectorCell — multi-select with draft-on-close commit
// ---------------------------------------------------------------------------

export type DataTableEntitySelectorCellProps = Omittable<{
  /** EntitySelector configuration (pools, search placeholder, etc.) */
  config: EntitySelectorConfig;
  /** Currently committed IDs */
  value: string[];
  /** Called on popover close if selections changed */
  onCommit: (ids: string[]) => void;
  align?: DataTableCellAlignment;
  disabled?: boolean;
  contentProps?: Omit<React.ComponentProps<typeof PopoverPopup>, 'children'>;
  /** Trigger display content */
  children: React.ReactNode;
}>;

export function DataTableEntitySelectorCell({
  config,
  value,
  onCommit,
  align,
  disabled,
  contentProps,
  children,
}: DataTableEntitySelectorCellProps) {
  const draftRef = React.useRef<string[]>(value);
  const [draftIds, setDraftIds] = React.useState<string[]>(value);

  // Build a lookup map from all pools
  const entityById = React.useMemo(() => {
    const map = new Map<string, { item: EntityItemConfig; poolId: string }>();
    for (const pool of config.pools) {
      for (const item of pool.items) {
        map.set(item.id, { item, poolId: pool.id });
      }
    }
    return map;
  }, [config.pools]);

  const selections = React.useMemo<EntitySelection[]>(
    () =>
      draftIds
        .map((id) => {
          const entry = entityById.get(id);
          if (!entry) return null;
          return { id, poolId: entry.poolId, entity: entry.item };
        })
        .filter(Boolean) as EntitySelection[],
    [draftIds, entityById]
  );

  const handleValueChange = React.useCallback((next: EntitySelection[]) => {
    const nextIds = next.map((s) => s.id);
    draftRef.current = nextIds;
    setDraftIds(nextIds);
  }, []);

  const handleOpenChange = React.useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) {
        // Reset draft to committed value on open
        draftRef.current = value;
        setDraftIds(value);
        return;
      }
      // Commit if changed
      const prev = new Set(value);
      const next = new Set(draftRef.current);
      if (prev.size !== next.size || [...prev].some((id) => !next.has(id))) {
        onCommit(draftRef.current);
      }
    },
    [value, onCommit]
  );

  return (
    <DataTablePopoverCell
      align={align}
      disabled={disabled}
      onOpenChange={handleOpenChange}
      contentProps={{
        className: 'p-0 w-auto min-w-96',
        ...contentProps,
      }}
      content={
        <EntitySelector
          variant="inline"
          config={config}
          value={selections}
          onValueChange={handleValueChange}
        >
          <EntitySelectorInput />
          <EntitySelectorSelectedChips label="Selected" />
          {config.pools.map((pool) => (
            <EntitySelectorPool key={pool.id} value={pool.id} />
          ))}
        </EntitySelector>
      }
    >
      {children}
    </DataTablePopoverCell>
  );
}

export { DataTableCellTrigger };
export type { DataTableCellTriggerProps };
